import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {eventOneClickPromotionPanelExtraReducers} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel-extra-reducers';
import type {
  EventOneClickPromoBackendResponse,
  EventOneClickPromoReduxState} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel.types';
import {
  OneClickPromoItemState,
} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel.types';
import {getDummyEventItem} from '@Libraries/events.library';
import {LoadingStates} from '@Utils/loading.util';
import {getDummySocialPostForSkeleton} from '@Libraries/social-media';
import {getDummyEmailCampaign} from '@Libraries/email-campaigns-library';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {MailingListVO} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import type {SocialPost} from '@Components/social-media/post.vo';

const initialState: EventOneClickPromoReduxState = {
  event: getDummyEventItem(),
  loadingState: LoadingStates.NOT_LOADED,
  socialPost: getDummySocialPostForSkeleton(),
  campaign: getDummyEmailCampaign(),
  emailPublishFailMessage: '',
  postPublishFailMessage: '',
  showCampaignsLoader: false,
  showPostLoader: false,
  allSocialAccounts: [],
  allMailingLists: [],
  campaignOneClickState: OneClickPromoItemState.DEFAULT,
  socialPostOneCLickState: OneClickPromoItemState.DEFAULT,
  emailSends: 0
};

const eventOneClickPromotionPanelSlice = createSlice({
  name: 'eventOneClickPromo',
  initialState,
  reducers: {
    setOneClickDataForEvent: (state, {payload}: PayloadAction<EventOneClickPromoBackendResponse>): void => {
      state.event = payload.event;
      state.campaign = payload.campaign;
      state.socialPost = payload.socialPost;
      state.allSocialAccounts = payload.accounts;
      state.allMailingLists = payload.mailingLists;
      state.emailSends = payload.emailSends;
    },

    setSocialPostCaption: (state, action: PayloadAction<string>): void => {
      state.socialPost.caption = action.payload;
    },

    setEmailCampaignSubject: (state, action: PayloadAction<string>): void => {
      state.campaign.subject = action.payload;
    },

    setLoadingState: (state, action: PayloadAction<LoadingStates>): void => {
      state.loadingState = action.payload;
    },

    addAccountToOneClickPost: (state, action: PayloadAction<SocialAccount>): void => {
      state.socialPost.accounts.push(action.payload);
    },

    removeAccountFromOneClickPost: (state, action: PayloadAction<SocialAccount>): void => {
      state.socialPost.accounts = state.socialPost.accounts.filter((account) => {
        return account.id !== action.payload.id;
      });
    },

    addMailingListToOneClickCampaign: (state, action: PayloadAction<MailingListVO>): void => {
      state.campaign.mailingLists.push(action.payload);
    },

    removeMailingListFromOneClickCampaign: (state, action: PayloadAction<MailingListVO>): void => {
      state.campaign.mailingLists = state.campaign.mailingLists.filter((mailingList) => {
        return action.payload.id !== mailingList.id;
      });
    },

    setShowCampaignLoader: (state, action: PayloadAction<boolean>): void => {
      state.showCampaignsLoader = action.payload;
    },

    setShowSocialPostsLoader: (state, action: PayloadAction<boolean>): void => {
      state.showPostLoader = action.payload;
    },

    setEmailCampaign: (state, action: PayloadAction<EmailCampaign>): void => {
      state.campaign = action.payload;
    },

    setSocialPost: (state, action: PayloadAction<SocialPost>): void => {
      state.socialPost = action.payload;
    },

    setCampaignOneClickState: (state, action: PayloadAction<OneClickPromoItemState>): void => {
      state.campaignOneClickState = action.payload;
    },

    setSocialPostOneClickState: (state, action: PayloadAction<OneClickPromoItemState>): void => {
      state.socialPostOneCLickState = action.payload;
    },

    setEmailCampaignErrorMessage: (state, action: PayloadAction<string>): void => {
      state.emailPublishFailMessage = action.payload;
    },

    doResetOneClickState: (state): void  => {
      state.campaignOneClickState = OneClickPromoItemState.DEFAULT;
      state.socialPostOneCLickState = OneClickPromoItemState.DEFAULT;
      state.postPublishFailMessage = '';
      state.emailPublishFailMessage = '';
    },

    addMailingListForOneClickFlow: (state, action: PayloadAction<MailingListVO>): void => {
      state.allMailingLists.push(action.payload);
    },

    addSocialAccountForOneClickFlow: (state, action: PayloadAction<SocialAccount>): void => {
      state.allSocialAccounts.push(action.payload);
    },

    setSocialAccountsForOneClickFlow: (state, action: PayloadAction<SocialAccount[]>): void => {
      state.allSocialAccounts = action.payload;
    }
  },
  extraReducers: eventOneClickPromotionPanelExtraReducers,
});

export const {
  setSocialPostCaption,
  setEmailCampaignSubject,
  setOneClickDataForEvent,
  setLoadingState,
  addAccountToOneClickPost,
  removeAccountFromOneClickPost,
  addMailingListToOneClickCampaign,
  removeMailingListFromOneClickCampaign,
  setShowSocialPostsLoader,
  setShowCampaignLoader,
  setSocialPost,
  setEmailCampaign,
  setCampaignOneClickState,
  setSocialPostOneClickState,
  setEmailCampaignErrorMessage,
  doResetOneClickState,
  addMailingListForOneClickFlow,
  addSocialAccountForOneClickFlow,
  setSocialAccountsForOneClickFlow
} = eventOneClickPromotionPanelSlice.actions;
export const eventOneClickPromotionReducer = eventOneClickPromotionPanelSlice.reducer;
